<script setup>
import { ref, computed, watch, nextTick, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { maskDateInput, displayToIso, isoToDisplay } from '../domain/dateMask.js';
import { DATE_MIN, DATE_MAX, classifyIso, birthStartYear } from '../domain/dateField.js';
import DatePickerCard from './DatePickerCard.vue';

/* Datumsfeld als Text, immer "TT.MM.JJJJ" — unabhängig von der Browsersprache (das native
   <input type="date"> zeigt in den USA MM/TT/JJ) — mit Kalenderfenster über das Symbol im Feld.
   Wert nach außen ist ISO ("JJJJ-MM-TT"); leer, solange die Eingabe unvollständig ist, kein echtes
   Datum ist oder außerhalb von min/max liegt (so wird nie ein ungültiges Datum gespeichert).
   `text` ist der getippte Text, `problem` der Grund für ein leeres Ergebnis bei vollständiger oder
   halber Eingabe: '' | 'invalid' | 'tooEarly' | 'tooLate'. Beides lässt die aufrufende Stelle eigene
   Meldungen bauen (NewPlayerDialog: Geburtsdatum). Ohne show-error=false zeigt das Feld selbst eine
   kurze Meldung, nachdem man es verlassen hat. Weitere Attribute (id, aria-*, @blur) gehen ans
   Eingabefeld, class und style an die Hülle. */
defineOptions({ inheritAttrs: false });

const props = defineProps({
  min: { type: String, default: DATE_MIN },
  max: { type: String, default: DATE_MAX },
  required: { type: Boolean, default: false },
  /** Jahr, in dem der Kalender bei leerem Feld öffnet (z. B. Geburtsdaten: birthStartYear()). */
  startYear: { type: Number, default: null },
  /** Kurzform für Geburtsdaten: startYear = heute minus 16 Jahre. */
  birth: { type: Boolean, default: false },
  showError: { type: Boolean, default: true },
});

const iso = defineModel({ type: String, default: '' });
const text = defineModel('text', { type: String, default: '' });
const problem = defineModel('problem', { type: String, default: '' });

const { t } = useI18n();
const limits = computed(() => ({ min: props.min, max: props.max }));
const start = computed(() => (props.birth ? birthStartYear() : props.startYear));

const field = ref(null);
const opener = ref(null);
const wrap = ref(null);
const shown = ref(isoToDisplay(iso.value));
const touched = ref(false);
const open = ref(false);
text.value = shown.value;

/* Ergebnis zu einem getippten Text: ISO nur, wenn echtes Datum innerhalb der Grenzen */
function evaluate(masked) {
  const candidate = displayToIso(masked);
  if (!masked) return { iso: '', problem: '' };
  if (!candidate) return { iso: '', problem: 'invalid' };
  const p = classifyIso(candidate, limits.value);
  return p ? { iso: '', problem: p } : { iso: candidate, problem: '' };
}

function apply(masked) {
  shown.value = masked;
  text.value = masked;
  const r = evaluate(masked);
  iso.value = r.iso;
  problem.value = r.problem;
}

function onInput(event) {
  const masked = maskDateInput(event.target.value);
  /* Auch den Feldinhalt selbst nachziehen: bei unverändertem Text (z. B. Buchstabe getippt) löst
     Vue sonst kein Update aus, und der Buchstabe bliebe stehen. */
  event.target.value = masked;
  apply(masked);
}

/* Wert von außen geändert (z. B. Formular zurückgesetzt, Kalender): Anzeige nachziehen. */
watch(iso, (value) => {
  if (value && value === displayToIso(shown.value)) return;
  if (!value && shown.value && problem.value) return; // halbe oder ungültige Eingabe bleibt stehen
  shown.value = isoToDisplay(value);
  text.value = shown.value;
  problem.value = '';
});

function onBlur() {
  touched.value = true;
}

const message = computed(() => {
  if (!props.showError || !problem.value) return '';
  if (!touched.value && shown.value.length < 10) return '';
  if (problem.value === 'tooEarly') return t('dateField.error.tooEarly', { min: isoToDisplay(props.min) });
  if (problem.value === 'tooLate') return t('dateField.error.tooLate', { max: isoToDisplay(props.max) });
  return t('dateField.error.invalid');
});

/* --- Kalenderfenster: Handy (bis 600 px) als Dialog, sonst als Fenster direkt am Feld --- */
const isMobile = ref(false);
let mq = null;
const syncMobile = () => { isMobile.value = !!mq && mq.matches; };
onMounted(() => {
  if (typeof window === 'undefined' || !window.matchMedia) return;
  mq = window.matchMedia('(max-width: 599.98px)');
  syncMobile();
  mq.addEventListener?.('change', syncMobile);
});
onBeforeUnmount(() => mq?.removeEventListener?.('change', syncMobile));

function toggle() {
  open.value = !open.value;
}

function onOk(value) {
  touched.value = true;
  apply(isoToDisplay(value));
  open.value = false;
}

/* Beim Schließen zurück ins Textfeld, egal wie geschlossen wurde (OK, Cancel, Esc, Klick daneben) */
watch(open, (isOpen) => {
  if (!isOpen) nextTick(() => field.value?.focus?.());
});

defineExpose({ focus: () => field.value?.focus(), open: () => { open.value = true; } });
</script>

<template>
  <div ref="wrap" class="df" :class="$attrs.class" :style="$attrs.style">
    <div class="df-box">
      <input
        v-bind="{ ...$attrs, class: undefined, style: undefined }"
        ref="field"
        type="text"
        inputmode="numeric"
        autocomplete="off"
        maxlength="10"
        class="df-input"
        :required="required"
        :value="shown"
        :placeholder="$t('dateField.placeholder')"
        :aria-invalid="message ? 'true' : $attrs['aria-invalid']"
        @input="onInput"
        @blur="onBlur"
      />
      <button
        ref="opener"
        type="button"
        class="df-open"
        data-testid="date-field-open"
        :aria-label="$t('dateField.open')"
        aria-haspopup="dialog"
        :aria-expanded="open ? 'true' : 'false'"
        @click="toggle"
      >
        <!-- Umriss, nicht gefüllt -->
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <rect x="3.5" y="5" width="17" height="15" rx="2.5" />
          <path d="M3.5 10h17M8 3v4M16 3v4" />
        </svg>
      </button>
    </div>
    <p v-if="message" class="df-error" role="alert" data-testid="date-field-error">{{ message }}</p>

    <v-dialog v-if="isMobile" v-model="open" width="auto" content-class="df-dialog">
      <DatePickerCard
        v-if="open"
        :iso="iso"
        :min="min"
        :max="max"
        :required="required"
        :start-year="start"
        @ok="onOk"
        @cancel="open = false"
      />
    </v-dialog>
    <v-menu
      v-else
      v-model="open"
      :activator="opener"
      :target="wrap"
      :close-on-content-click="false"
      location="bottom end"
      :offset="6"
      content-class="df-menu"
    >
      <DatePickerCard
        v-if="open"
        :iso="iso"
        :min="min"
        :max="max"
        :required="required"
        :start-year="start"
        @ok="onOk"
        @cancel="open = false"
      />
    </v-menu>
  </div>
</template>

<style scoped>
.df {
  position: relative;
  min-width: 0;
}
.df-box {
  position: relative;
}
/* Doppelte Klasse: die Feldstile der Umgebung (".edit-grid .field input", ".ti-field input" …) haben mehr Gewicht
   als eine einzelne Klasse und würden den Platz für das Symbol wegnehmen. */
.df .df-input {
  width: 100%;
  box-sizing: border-box;
  padding-right: 40px;
}
.df-open {
  position: absolute;
  top: 50%;
  right: 4px;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: var(--muted);
  cursor: pointer;
}
.df-open:hover {
  color: var(--green);
}
.df-open:focus-visible {
  outline: 2px solid var(--green);
  outline-offset: 1px;
}
.df-error {
  margin: 2px 0 0;
  font-size: 0.74rem;
  line-height: 1.4;
  color: var(--red);
}
</style>
